import React, { useEffect, useMemo, useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Tab,
  Tabs,
  Spinner,
  Stack,
} from "react-bootstrap";
import {
  FaArrowDown,
  FaArrowUp,
  FaFilePdf,
  FaMagic,
  FaPlus,
  FaSave,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import jsPDF from "jspdf";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { parseContractTemplateWithGemini } from "../../services/gestionDocumentos/contractTemplateService";
import { canonicalizeContractVariableKey } from "../../services/gestionDocumentos/contractVariableSchema";
import { logBusinessAction } from "../../services/AuditService";
import {
  A4_WIDTH_PT,
  DATOS_INICIALES,
  PDF_MARGIN_PRESETS,
  PLANTILLA_INICIAL,
  PT_TO_MM,
  PX_TO_PT,
  QUILL_FORMATS,
  QUILL_MODULES,
  STEP_KEYS,
  TOKENS_FRECUENTES,
  buildContratoPdfHtml,
  buildContratoSections,
  calcularFinPeriodoPrueba,
  extractOriginalContractTitle,
  extractPageTextPreservingFormat,
  formatDateEsPe,
  formatMonto,
  formatSoles,
  getFullWorkerName,
  getUserRuc,
  hasRenderableText,
  htmlToTextPreserveBoldMarkers,
  isHtmlContent,
  isIntroClause,
  isSignatureClause,
  normalizeParsedClauseContent,
  normalizeDisplayLine,
  normalizeQuillHtml,
  plainTextToEditorHtml,
  renderInlineBold,
  renderRichParagraphs,
  sanitizeEditorHtml,
  splitParagraphsRaw,
  stripBoldMarkers,
  toDateText,
  toIsoDateString,
  toSafeText,
  toTemplateKey,
  variableLabelFor,
} from "./contratos/contractBuilderUtils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const db = getFirestore(firebaseApp);
const TEMPLATE_DEFAULT_DATA_KEYS = [
  "empresa",
  "ruc_empresa",
  "empleador_domicilio_legal",
  "representante_legal",
  "representante_legal_cargo",
  "firma_empleador_nombre",
  "firma_empleador_cargo",
  "ciudad_jurisdiccion",
  "ciudad_firma",
  "fecha_firma",
  "tipo_contrato",
  "jornada",
  "lugar_trabajo",
  "periodo_prueba_meses",
  "fecha_fin_periodo_prueba",
];

function GeneradorContratos({ usuario }) {
  const templateKey = toTemplateKey(usuario?.ruc);
  const urlParams = new URLSearchParams(window.location.search);
  const templateIdFromUrl = toSafeText(urlParams.get("plantillaId"));
  const isGenerateFlow = false; // Forzado a false para mostrar siempre las 5 pestañas
  const visibleStepKeys = STEP_KEYS;

  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [ingestaStatus, setIngestaStatus] = useState("");
  const [ingestaError, setIngestaError] = useState("");
  const [analizando, setAnalizando] = useState(false);

  const [templateName, setTemplateName] = useState("CONTRATO DE TRABAJO");
  const [contractType, setContractType] = useState("GENERAL");
  const [legalAlerts, setLegalAlerts] = useState([]);

  const [clausulas, setClausulas] = useState(() => {
    try {
      const saved = localStorage.getItem(templateKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
        if (Array.isArray(parsed?.clauses)) return parsed.clauses;
      }
    } catch (error) {
      console.error("Error leyendo plantilla de contratos:", error);
    }
    return PLANTILLA_INICIAL;
  });

  const [variablesConfig, setVariablesConfig] = useState(() =>
    TOKENS_FRECUENTES.map((key) => ({ key, label: variableLabelFor(key), confirmed: true, sourceText: "" }))
  );

  const [datos, setDatos] = useState(DATOS_INICIALES);
  const [saveMessage, setSaveMessage] = useState("");
  const [activeStep, setActiveStep] = useState(isGenerateFlow ? "paso-5" : STEP_KEYS[0]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [contractTitle, setContractTitle] = useState("CONTRATO DE TRABAJO");
  const [pdfMarginPreset, setPdfMarginPreset] = useState("NORMAL");
  const [trabajadores, setTrabajadores] = useState([]);
  const [loadingTrabajadores, setLoadingTrabajadores] = useState(false);
  const [selectedTrabajadorId, setSelectedTrabajadorId] = useState("");
  const [selectedTrabajadorIds, setSelectedTrabajadorIds] = useState([]);
  const [generationMode, setGenerationMode] = useState("INDIVIDUAL");
  const [trabajadoresError, setTrabajadoresError] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(templateKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (!parsed || Array.isArray(parsed)) return;

      if (parsed.templateName) setTemplateName(parsed.templateName);
      if (parsed.contractType) setContractType(parsed.contractType);
      if (parsed.contractTitle) setContractTitle(parsed.contractTitle);
      if (parsed.pdfMarginPreset && PDF_MARGIN_PRESETS[parsed.pdfMarginPreset]) {
        setPdfMarginPreset(parsed.pdfMarginPreset);
      }
    } catch (error) {
      console.error("Error leyendo metadata de plantilla local:", error);
    }
  }, [templateKey]);

  useEffect(() => {
    const loadPdfJs = async () => {
      try {
        if (window.pdfjsLib) {
          setPdfJsLoaded(true);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js";
        script.async = true;
        script.crossOrigin = "anonymous";

        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error("Timeout cargando PDF.js")), 25000);
          document.head.appendChild(script);
        });

        if (!window.pdfjsLib) throw new Error("No se pudo inicializar PDF.js");

        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
        setPdfJsLoaded(true);
      } catch (error) {
        setIngestaError(`No se pudo cargar PDF.js: ${error.message}`);
      }
    };

    loadPdfJs();
  }, []);

  useEffect(() => {
    if (!pdfFile) return () => { };
    const objectUrl = URL.createObjectURL(pdfFile);
    setPdfUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [pdfFile]);

  useEffect(() => {
    let active = true;
    const loadCompanyDefaults = async () => {
      const ruc = getUserRuc(usuario);
      let companyRep = "";
      let companyCargo = "";
      let companyDir = "";
      if (ruc) {
        try {
          const compSnap = await getDoc(doc(db, "company", ruc));
          if (compSnap.exists()) {
            const cData = compSnap.data() || {};
            companyRep = toSafeText(cData.representante_legal || cData.representanteLegal || cData.representante || "");
            companyCargo = toSafeText(cData.representante_legal_cargo || cData.representanteCargo || cData.cargoRepresentante || "");
            companyDir = toSafeText(cData.direccion || "");
          }
        } catch (e) {
          console.warn("No se pudo cargar datos de company:", e);
        }
      }

      if (!active) return;
      setDatos((prev) => {
        const rep = prev.representante_legal || prev.firma_empleador_nombre || companyRep || usuario?.representante_legal || "";
        const repCargo = prev.representante_legal_cargo || prev.firma_empleador_cargo || companyCargo || usuario?.representante_legal_cargo || "Gerente General";
        return {
          ...prev,
          empresa: prev.empresa || usuario?.empresa || usuario?.empresaNombreComercial || "",
          ruc_empresa: prev.ruc_empresa || ruc || "",
          empleador_domicilio_legal: prev.empleador_domicilio_legal || companyDir || usuario?.direccion || "",
          representante_legal: rep,
          firma_empleador_nombre: rep,
          representante_legal_cargo: repCargo,
          firma_empleador_cargo: repCargo,
        };
      });
    };

    loadCompanyDefaults();
    return () => {
      active = false;
    };
  }, [usuario]);

  const loadTrabajadores = async () => {
    const rucEmpresa = getUserRuc(usuario);
    if (!rucEmpresa) {
      setTrabajadores([]);
      setSelectedTrabajadorId("");
      setTrabajadoresError("No se pudo identificar el RUC de la empresa.");
      return;
    }

    try {
      setLoadingTrabajadores(true);
      setTrabajadoresError("");

      const byRucEmpresaQuery = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", rucEmpresa));
      const byRucQuery = query(collection(db, "pl_empleados"), where("ruc", "==", rucEmpresa));
      const [byRucEmpresaSnap, byRucSnap] = await Promise.all([
        getDocs(byRucEmpresaQuery),
        getDocs(byRucQuery),
      ]);

      const mergedMap = new Map();
      [...byRucEmpresaSnap.docs, ...byRucSnap.docs].forEach((docRef) => {
        mergedMap.set(docRef.id, { id: docRef.id, ...docRef.data() });
      });

      const rows = Array.from(mergedMap.values())
        .sort((a, b) => getFullWorkerName(a).localeCompare(getFullWorkerName(b), "es", { sensitivity: "base" }));

      setTrabajadores(rows);
      if (!rows.length) {
        setSelectedTrabajadorId("");
        setSelectedTrabajadorIds([]);
        return;
      }

      const exists = rows.some((row) => row.id === selectedTrabajadorId);
      const nextSelectedId = exists ? selectedTrabajadorId : rows[0].id;
      setSelectedTrabajadorId(nextSelectedId);
      setSelectedTrabajadorIds((prev) => {
        const filtered = prev.filter((id) => rows.some((row) => row.id === id));
        if (filtered.length) return filtered;
        return nextSelectedId ? [nextSelectedId] : [];
      });
    } catch (error) {
      console.error("Error cargando trabajadores:", error);
      setTrabajadoresError("No se pudieron cargar los trabajadores desde planillas.");
    } finally {
      setLoadingTrabajadores(false);
    }
  };

  useEffect(() => {
    loadTrabajadores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.ruc, usuario?.ruc_empresa, usuario?.RUC, usuario?.rucEmpresa]);

  useEffect(() => {
    if (activeStep !== "paso-5") return;
    loadTrabajadores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    if (generationMode !== "INDIVIDUAL") return;
    if (!selectedTrabajadorId) return;
    setSelectedTrabajadorIds([selectedTrabajadorId]);
  }, [generationMode, selectedTrabajadorId]);

  useEffect(() => {
    if (!isGenerateFlow) return;
    setGenerationMode("MASIVO");
    setActiveStep("paso-5");
  }, [isGenerateFlow]);

  const applyCloudTemplateData = (templateData) => {
    const safeTemplate = templateData || {};
    const rawClauses = Array.isArray(safeTemplate.clauses) ? safeTemplate.clauses : [];
    if (!rawClauses.length) {
      setSaveMessage("La plantilla seleccionada no tiene cláusulas para cargar.");
      return;
    }

    const normalizedClauses = rawClauses.map((clause, index) => ({
      id: clause.id || `clausula-${Date.now()}-${index}`,
      title: String(clause.title || `CLÁUSULA ${index + 1}`),
      content: normalizeParsedClauseContent(clause.title || "", clause.content || ""),
    }));

    const savedVariables = Array.isArray(safeTemplate.variables) ? safeTemplate.variables : [];
    const mergedKeys = new Set(TOKENS_FRECUENTES);
    savedVariables.forEach((item) => mergedKeys.add(canonicalizeContractVariableKey(item?.key || item?.label || "")));
    normalizedClauses.forEach((clause) => {
      const matches = String(clause.content || "").match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) || [];
      matches.forEach((match) => mergedKeys.add(canonicalizeContractVariableKey(match.replace(/[{}\s]/g, ""))));
    });

    const variableMap = new Map();
    savedVariables.forEach((item) => {
      const key = canonicalizeContractVariableKey(item?.key || item?.label || item?.sourceText || "");
      if (!key) return;
      variableMap.set(key, {
        key,
        label: item?.label || variableLabelFor(key),
        confirmed: item?.confirmed !== false,
        sourceText: item?.sourceText || "",
      });
    });

    const nextVariables = Array.from(mergedKeys)
      .filter(Boolean)
      .map((key) => variableMap.get(key) || { key, label: variableLabelFor(key), confirmed: true, sourceText: "" });

    setTemplateName(safeTemplate.templateName || "CONTRATO DE TRABAJO");
    setContractType(safeTemplate.contractType || "GENERAL");
    setContractTitle(
      safeTemplate.contractTitle ||
      extractOriginalContractTitle(safeTemplate.templateName || "CONTRATO DE TRABAJO", normalizedClauses)
    );
    setPdfMarginPreset(PDF_MARGIN_PRESETS[safeTemplate.pdfMarginPreset] ? safeTemplate.pdfMarginPreset : "NORMAL");
    setClausulas(normalizedClauses);
    setVariablesConfig(nextVariables);
    setDatos((prev) => {
      const next = { ...prev };
      nextVariables.forEach((variable) => {
        if (!next[variable.key] && variable.sourceText) {
          next[variable.key] = variable.sourceText;
        }
      });
      const templateDefaults = safeTemplate.templateDefaults && typeof safeTemplate.templateDefaults === "object"
        ? safeTemplate.templateDefaults
        : {};
      TEMPLATE_DEFAULT_DATA_KEYS.forEach((key) => {
        const incoming = toSafeText(templateDefaults[key]);
        if (incoming) next[key] = incoming;
      });
      if (next.representante_legal && !next.firma_empleador_nombre) next.firma_empleador_nombre = next.representante_legal;
      if (next.firma_empleador_nombre && !next.representante_legal) next.representante_legal = next.firma_empleador_nombre;
      if (next.representante_legal_cargo && !next.firma_empleador_cargo) next.firma_empleador_cargo = next.representante_legal_cargo;
      if (next.firma_empleador_cargo && !next.representante_legal_cargo) next.representante_legal_cargo = next.firma_empleador_cargo;
      next.empresa = next.empresa || usuario?.empresa || usuario?.empresaNombreComercial || "";
      next.ruc_empresa = next.ruc_empresa || usuario?.ruc || "";
      return next;
    });

    localStorage.setItem(
      templateKey,
      JSON.stringify({
        templateName: safeTemplate.templateName || "CONTRATO DE TRABAJO",
        contractType: safeTemplate.contractType || "GENERAL",
        contractTitle:
          safeTemplate.contractTitle ||
          extractOriginalContractTitle(safeTemplate.templateName || "CONTRATO DE TRABAJO", normalizedClauses),
        pdfMarginPreset: PDF_MARGIN_PRESETS[safeTemplate.pdfMarginPreset] ? safeTemplate.pdfMarginPreset : "NORMAL",
        clauses: normalizedClauses,
      })
    );

    setActiveStep("paso-5");
    setSaveMessage(`Plantilla cargada: ${safeTemplate.templateName || "Sin nombre"}.`);
  };

  useEffect(() => {
    if (!templateIdFromUrl) return;

    const loadTemplateFromUrl = async () => {
      try {
        const templateRef = doc(db, "contratoPlantillas", templateIdFromUrl);
        const templateSnap = await getDoc(templateRef);
        if (!templateSnap.exists()) {
          setSaveMessage("No se encontró la plantilla solicitada.");
          return;
        }
        applyCloudTemplateData({ id: templateSnap.id, ...templateSnap.data() });
      } catch (error) {
        console.error("Error cargando plantilla desde URL:", error);
        setSaveMessage("No se pudo cargar la plantilla seleccionada.");
      }
    };

    loadTemplateFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateIdFromUrl]);

  const placeholdersDetectados = useMemo(() => {
    const found = new Set(TOKENS_FRECUENTES);

    clausulas.forEach((c) => {
      const matches = c.content.match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) || [];
      matches.forEach((m) => found.add(canonicalizeContractVariableKey(m.replace(/{{|}}|\s/g, ""))));
    });

    variablesConfig.forEach((v) => found.add(canonicalizeContractVariableKey(v.key)));

    return Array.from(found).filter(Boolean);
  }, [clausulas, variablesConfig]);

  const contratoSections = useMemo(
    () => buildContratoSections(clausulas, datos, templateName, { boldVariables: true, contractTitle }),
    [clausulas, datos, templateName, contractTitle]
  );
  const selectedTrabajador = useMemo(
    () => trabajadores.find((worker) => worker.id === selectedTrabajadorId) || null,
    [trabajadores, selectedTrabajadorId]
  );
  const selectedTrabajadoresBulk = useMemo(
    () => trabajadores.filter((worker) => selectedTrabajadorIds.includes(worker.id)),
    [trabajadores, selectedTrabajadorIds]
  );
  const fechaInicioInputValue = useMemo(() => toIsoDateString(datos.fecha_inicio) || "", [datos.fecha_inicio]);
  const fechaFinInputValue = useMemo(() => toIsoDateString(datos.fecha_fin) || "", [datos.fecha_fin]);
  const selectedPdfMargin = PDF_MARGIN_PRESETS[pdfMarginPreset] || PDF_MARGIN_PRESETS.NORMAL;
  const leftRightPdfMarginPt = selectedPdfMargin.pageMarginPt + selectedPdfMargin.innerPaddingPx * PX_TO_PT;
  const printableWidthPt = Math.max(A4_WIDTH_PT - leftRightPdfMarginPt * 2, 220);
  const printableWidthPct = Math.max(20, Math.min(100, (printableWidthPt / A4_WIDTH_PT) * 100));
  const sideMarginPct = (100 - printableWidthPct) / 2;
  const sideMarginMm = leftRightPdfMarginPt * PT_TO_MM;

  const extractTextFromPdf = async (file) => {
    if (!window.pdfjsLib) throw new Error("PDF.js no está disponible.");

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;

    const pages = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const pageText = extractPageTextPreservingFormat(content);
      pages.push(`--- PAGINA ${pageNum} ---\n${pageText}`);
    }

    return pages.join("\n\n");
  };

  const updateClausula = (id, field, value) => {
    setClausulas((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const addClausula = () => {
    setClausulas((prev) => [
      ...prev,
      {
        id: `clausula-${Date.now()}`,
        title: `Nueva cláusula ${prev.length + 1}`,
        content: "",
      },
    ]);
  };

  const removeClausula = (id) => {
    setClausulas((prev) => prev.filter((c) => c.id !== id));
  };

  const moveClausula = (index, direction) => {
    setClausulas((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      if (isIntroClause(prev[index]) || isSignatureClause(prev[index])) return prev;

      let nextIndex = direction === "up" ? index - 1 : index + 1;
      while (nextIndex >= 0 && nextIndex < prev.length) {
        if (!isIntroClause(prev[nextIndex]) && !isSignatureClause(prev[nextIndex])) break;
        nextIndex += direction === "up" ? -1 : 1;
      }
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const copy = [...prev];
      [copy[index], copy[nextIndex]] = [copy[nextIndex], copy[index]];
      return copy;
    });
  };

  const insertToken = (clausulaId, token) => {
    const placeholder = `{{${token}}}`;
    setClausulas((prev) =>
      prev.map((c) => {
        if (c.id !== clausulaId) return c;
        if (isHtmlContent(c.content)) {
          const html = normalizeQuillHtml(c.content);
          return { ...c, content: html ? `${html}<p>${placeholder}</p>` : `<p>${placeholder}</p>` };
        }
        return { ...c, content: `${c.content} ${placeholder}`.trim() };
      })
    );
  };

  const toggleVariableConfirmed = (key) => {
    setVariablesConfig((prev) => prev.map((v) => (v.key === key ? { ...v, confirmed: !v.confirmed } : v)));
  };

  const updateVariableLabel = (key, label) => {
    setVariablesConfig((prev) => prev.map((v) => (v.key === key ? { ...v, label } : v)));
  };

  const saveTemplateLocal = () => {
    localStorage.setItem(
      templateKey,
      JSON.stringify({
        templateName,
        contractType,
        contractTitle,
        pdfMarginPreset,
        clauses: clausulas,
      })
    );
    setSaveMessage("Modelo guardado localmente.");
  };

  const saveTemplateCloud = async () => {
    const ruc = getUserRuc(usuario);
    if (!ruc) {
      setSaveMessage("No se pudo guardar: falta RUC de empresa en el usuario.");
      return;
    }

    const confirmedVariables = variablesConfig.filter((v) => v.confirmed);
    const templateDefaults = TEMPLATE_DEFAULT_DATA_KEYS.reduce((acc, key) => {
      const value = toSafeText(datos?.[key]);
      if (value) acc[key] = value;
      return acc;
    }, {});
    const basePayload = {
      ruc,
      ruc_empresa: ruc,
      empresa: usuario?.empresa || usuario?.empresaNombreComercial || "",
      templateName,
      contractType,
      contractTitle,
      pdfMarginPreset,
      clauses: clausulas,
      variables: confirmedVariables,
      templateDefaults,
      createdBy: usuario?.email || "",
      updatedAt: serverTimestamp(),
    };

    try {
      if (templateIdFromUrl) {
        await setDoc(
          doc(db, "contratoPlantillas", templateIdFromUrl),
          {
            ...basePayload,
            // Preserva createdAt si ya existe; si no existe lo crea.
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        try {
          await logBusinessAction("ACTUALIZACION_PLANTILLA_CONTRATO", "GestionDocumentos", {
            plantillaId: templateIdFromUrl,
            templateName,
            contractType,
            cantidadClausulas: clausulas.length,
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de actualización", e);
        }

        setSaveMessage("Plantilla actualizada correctamente en la nube.");
        return;
      }

      await addDoc(collection(db, "contratoPlantillas"), {
        ...basePayload,
        createdAt: serverTimestamp(),
      });

      try {
        await logBusinessAction("CREACION_PLANTILLA_CONTRATO", "GestionDocumentos", {
          templateName,
          contractType,
          cantidadClausulas: clausulas.length,
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría", e);
      }

      setSaveMessage("Plantilla guardada en la nube para esta empresa.");
    } catch (error) {
      console.error("Error guardando plantilla en nube:", error);
      setSaveMessage(`No se pudo guardar la plantilla: ${error?.message || "error desconocido"}`);
    }
  };

  const buildDatosForWorker = (baseDatos, worker) => {
    if (!worker) return { ...baseDatos };
    const fullName = getFullWorkerName(worker);
    const workerDni = toSafeText(worker.dni || worker.id);
    const workerAddress = toSafeText(worker.domicilio);
    const workerPhone = toSafeText(worker.telefono || worker.phone || worker.celular);
    const workerEmail = toSafeText(worker.email || worker.correo);
    const workerPosition = toSafeText(worker.cargo);
    const rawSalary = toSafeText(worker.sueldo_base || worker.sueldo_basico);
    const workerSalary = rawSalary ? formatSoles(rawSalary) : (baseDatos.sueldo || "");
    const workerSalaryNum = rawSalary ? formatMonto(rawSalary) : (baseDatos.monto_sueldo || "");
    const workerStartDate = toDateText(worker.fecha_ingreso || worker.fecha_reingreso);
    const fechaInicio = baseDatos.fecha_inicio || workerStartDate || "";
    const finPruebaIso = fechaInicio ? calcularFinPeriodoPrueba(fechaInicio, baseDatos.periodo_prueba_meses || 3) : "";
    const finPruebaTexto = finPruebaIso ? formatDateEsPe(finPruebaIso) : "";

    const rep = toSafeText(baseDatos.representante_legal || baseDatos.firma_empleador_nombre || usuario?.representante_legal);
    const repCargo = toSafeText(baseDatos.representante_legal_cargo || baseDatos.firma_empleador_cargo || usuario?.representante_legal_cargo || "Gerente General");

    return {
      ...baseDatos,
      empresa: toSafeText(baseDatos.empresa || usuario?.empresa || usuario?.empresaNombreComercial),
      ruc_empresa: toSafeText(baseDatos.ruc_empresa || usuario?.ruc),
      empleador_domicilio_legal: toSafeText(baseDatos.empleador_domicilio_legal || usuario?.direccion),
      representante_legal: rep,
      representante_legal_cargo: repCargo,
      trabajador_nombre: fullName || baseDatos.trabajador_nombre || "",
      trabajador_dni: workerDni || baseDatos.trabajador_dni || "",
      trabajador_domicilio: workerAddress || baseDatos.trabajador_domicilio || "",
      trabajador_telefono: workerPhone || baseDatos.trabajador_telefono || "",
      trabajador_correo: workerEmail || baseDatos.trabajador_correo || "",
      telefono: workerPhone || baseDatos.telefono || "",
      correo: workerEmail || baseDatos.correo || "",
      lugar_trabajo: workerAddress || baseDatos.lugar_trabajo || "",
      cargo: workerPosition || baseDatos.cargo || "",
      sueldo: workerSalary,
      monto_sueldo: workerSalaryNum,
      fecha_inicio: fechaInicio,
      fecha_fin: baseDatos.fecha_fin || "",
      periodo_prueba_meses: toSafeText(baseDatos.periodo_prueba_meses) || "3",
      fecha_fin_periodo_prueba: baseDatos.fecha_fin_periodo_prueba || finPruebaTexto || "",
      firma_empleador_nombre: rep,
      firma_empleador_cargo: repCargo,
      firma_trabajador_nombre: fullName || baseDatos.firma_trabajador_nombre || "",
      firma_trabajador_dni: workerDni || baseDatos.firma_trabajador_dni || "",
    };
  };

  const applySelectedWorkerData = () => {
    if (!selectedTrabajador) return;

    const fullName = getFullWorkerName(selectedTrabajador);
    const workerDni = toSafeText(selectedTrabajador.dni || selectedTrabajador.id);

    setDatos((prev) => buildDatosForWorker(prev, selectedTrabajador));

    setSaveMessage(`Datos del trabajador aplicados: ${fullName || workerDni}.`);
  };

  const resetTemplate = () => {
    setTemplateName("CONTRATO DE TRABAJO");
    setContractType("GENERAL");
    setContractTitle("CONTRATO DE TRABAJO");
    setPdfMarginPreset("NORMAL");
    setLegalAlerts([]);
    setVariablesConfig(TOKENS_FRECUENTES.map((key) => ({ key, label: variableLabelFor(key), confirmed: true, sourceText: "" })));
    setClausulas(PLANTILLA_INICIAL);
    localStorage.setItem(
      templateKey,
      JSON.stringify({
        templateName: "CONTRATO DE TRABAJO",
        contractType: "GENERAL",
        contractTitle: "CONTRATO DE TRABAJO",
        pdfMarginPreset: "NORMAL",
        clauses: PLANTILLA_INICIAL,
      })
    );
    setSaveMessage("Plantilla restablecida.");
  };

  const buildPdfFileName = (contractData = datos) =>
    `Contrato_${(contractData.trabajador_nombre || contractData.firma_trabajador_nombre || "Trabajador").replace(/\s+/g, "_")}.pdf`;

  const exportPdfFallback = (sections = contratoSections, fileName = buildPdfFileName()) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = selectedPdfMargin.pageMarginPt;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const textWidth = pageWidth - margin * 2;
    let y = margin;
    const lineHeight = 13;
    const paragraphGap = 6;
    const sectionGap = 12;

    const ensureSpace = (required = lineHeight) => {
      if (y + required > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const tokenizeStyledText = (text) => {
      const normalized = String(text || "")
        .replace(/\\\*/g, "*")
        .replace(/\*{3,}/g, "**");
      const parts = normalized.split(/(\*\*[\s\S]+?\*\*)/g);
      const tokens = [];
      parts.forEach((part) => {
        if (!part) return;
        const isBold = /^\*\*[\s\S]+?\*\*$/.test(part);
        const raw = (isBold ? part.slice(2, -2) : part).replace(/\*\*/g, "");
        raw.split(/(\s+)/).forEach((chunk) => {
          if (chunk) tokens.push({ text: chunk, bold: isBold });
        });
      });
      return tokens;
    };

    const tokenWidth = (text, bold) => {
      doc.setFont("times", bold ? "bold" : "normal");
      doc.setFontSize(10);
      return doc.getTextWidth(text);
    };

    const wrapStyledTokens = (tokens, maxWidth) => {
      const lines = [];
      let current = [];
      let currentWidth = 0;

      const flush = () => {
        while (current.length && /^\s+$/.test(current[current.length - 1].text)) current.pop();
        if (current.length) lines.push(current);
        current = [];
        currentWidth = 0;
      };

      tokens.forEach((token) => {
        let text = token.text;
        if (!current.length) text = text.replace(/^\s+/, "");
        if (!text) return;

        const width = tokenWidth(text, token.bold);
        if (current.length && currentWidth + width > maxWidth && !/^\s+$/.test(text)) {
          flush();
          text = text.replace(/^\s+/, "");
          if (!text) return;
        }

        current.push({ text, bold: token.bold });
        currentWidth += tokenWidth(text, token.bold);
      });

      flush();
      return lines;
    };

    const lineNaturalWidth = (lineTokens) =>
      lineTokens.reduce((total, token) => total + tokenWidth(token.text, token.bold), 0);

    const countWhitespaceTokens = (lineTokens) =>
      lineTokens.reduce((count, token) => (/\s+/.test(token.text) ? count + 1 : count), 0);

    const drawStyledLine = (lineTokens, { justify = false } = {}) => {
      let x = margin;
      const naturalWidth = lineNaturalWidth(lineTokens);
      const whitespaceCount = countWhitespaceTokens(lineTokens);

      let extraPerGap = 0;
      if (justify && whitespaceCount > 0) {
        const extra = Math.max(0, textWidth - naturalWidth);
        // Evita espaciados artificiales en líneas demasiado cortas.
        if (extra > 0 && naturalWidth > textWidth * 0.62) {
          extraPerGap = extra / whitespaceCount;
        }
      }

      lineTokens.forEach((token) => {
        const width = tokenWidth(token.text, token.bold);
        if (/\s+/.test(token.text)) {
          x += width + extraPerGap;
          return;
        }

        doc.setFont("times", token.bold ? "bold" : "normal");
        doc.setFontSize(10);
        doc.text(token.text, x, y);
        x += width;
      });
    };

    const writeParagraph = (rawText, { bold = false } = {}) => {
      const paragraph = String(rawText || "")
        .replace(/\\\*/g, "*")
        .replace(/\*{3,}/g, "**")
        .replace(/\r\n/g, "\n")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (!paragraph) return;

      const hasInlineBold = /\*\*[\s\S]+?\*\*/.test(paragraph);
      if (hasInlineBold && !bold) {
        const tokens = tokenizeStyledText(paragraph);
        const lines = wrapStyledTokens(tokens, textWidth);
        ensureSpace(lines.length * lineHeight + paragraphGap);

        lines.forEach((lineTokens, lineIndex) => {
          const shouldJustify = lineIndex < lines.length - 1;
          drawStyledLine(lineTokens, { justify: shouldJustify });
          y += lineHeight;
        });
        y += paragraphGap;
        return;
      }

      const text = stripBoldMarkers(paragraph).replace(/\*\*/g, "");
      const lines = doc.splitTextToSize(text, textWidth);
      ensureSpace(lines.length * lineHeight + paragraphGap);
      doc.setFont("times", bold ? "bold" : "normal");
      doc.setFontSize(10);
      doc.text(lines, margin, y, { align: "justify", maxWidth: textWidth });
      y += lines.length * lineHeight + paragraphGap;
    };

    const writeCenteredTitle = (title) => {
      const rawTitleLines = String(title || "")
        .split("\n")
        .map((line) => normalizeDisplayLine(line))
        .filter(Boolean);
      if (!rawTitleLines.length) return;
      const lines = rawTitleLines.flatMap((line) => doc.splitTextToSize(line, textWidth));
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      ensureSpace(lines.length * lineHeight + sectionGap);
      doc.text(lines, pageWidth / 2, y, { align: "center", maxWidth: textWidth });
      y += lines.length * lineHeight + sectionGap;
    };

    writeCenteredTitle(sections.title);

    const introForPdf = hasRenderableText(sections.introHtml)
      ? splitParagraphsRaw(htmlToTextPreserveBoldMarkers(sections.introHtml))
      : sections.introParagraphs;
    introForPdf.forEach((p) => writeParagraph(p));
    if (introForPdf.length) y += 4;

    sections.clauses.forEach((clause) => {
      const clauseParagraphsForPdf = hasRenderableText(clause.html)
        ? splitParagraphsRaw(htmlToTextPreserveBoldMarkers(clause.html))
        : clause.paragraphs;
      clauseParagraphsForPdf.forEach((p) => writeParagraph(p));
      y += sectionGap - paragraphGap;
    });

    const { employerName, employerRole, employeeName, employeeDni } = sections.signatures;
    if (employerName || employeeName) {
      ensureSpace(180);
      y += 62;
      const colWidth = (textWidth - 40) / 2;
      const leftX = margin;
      const rightX = margin + colWidth + 40;

      doc.setDrawColor(80);
      const lineY = y + 34;
      doc.line(leftX, lineY, leftX + colWidth, lineY);
      doc.line(rightX, lineY, rightX + colWidth, lineY);

      doc.setFont("times", "bold");
      doc.setFontSize(9);
      doc.text("EL EMPLEADOR", leftX + colWidth / 2, lineY + 14, { align: "center" });
      doc.text("EL TRABAJADOR", rightX + colWidth / 2, lineY + 14, { align: "center" });

      doc.setFont("times", "normal");
      if (employerName) doc.text(stripBoldMarkers(employerName), leftX + colWidth / 2, lineY + 28, { align: "center" });
      if (employeeName) doc.text(stripBoldMarkers(employeeName), rightX + colWidth / 2, lineY + 28, { align: "center" });

      if (employerRole) doc.text(stripBoldMarkers(employerRole), leftX + colWidth / 2, lineY + 42, { align: "center" });
      if (employeeDni) doc.text(`DNI: ${stripBoldMarkers(employeeDni)}`, rightX + colWidth / 2, lineY + 42, { align: "center" });
      y = lineY + 58;
    }

    doc.save(fileName);
  };

  const persistContractDates = async (contractData) => {
    const dniTrabajador = toSafeText(contractData.trabajador_dni || contractData.firma_trabajador_dni);
    const fechaInicioRaw = toSafeText(contractData.fecha_inicio);
    const fechaFinRaw = toSafeText(contractData.fecha_fin);
    const fechaInicioIso = toIsoDateString(fechaInicioRaw);
    const fechaFinIso = toIsoDateString(fechaFinRaw);
    const isContratoIndefinido = /indefinido|indeterminado|plazo\s+indeterminado/i.test(
      `${contractType} ${toSafeText(contractData.tipo_contrato)} ${templateName}`
    );

    if (!dniTrabajador) {
      return {
        ok: false,
        reason: "missing_dni",
      };
    }

    const commonContractFields = {
      contratoTipo: toSafeText(contractData.tipo_contrato) || contractType || "",
      contratoPlantilla: templateName || "",
      contratoFechaInicio: fechaInicioIso || "",
      contratoFechaFin: fechaFinIso || "",
      contratoFechaInicioTexto: fechaInicioRaw || "",
      contratoFechaFinTexto: fechaFinRaw || "",
      contratoGeneradoAt: serverTimestamp(),
    };

    const userPayload = {
      ...commonContractFields,
      fechaInicioContrato: fechaInicioIso || "",
      fechaFinContrato: fechaFinIso || "",
    };

    if (fechaFinIso) {
      userPayload.vencimientoContrato = fechaFinIso;
    } else if (isContratoIndefinido) {
      userPayload.vencimientoContrato = "";
    }

    const empleadoPayload = {
      ...commonContractFields,
      fecha_inicio_contrato: fechaInicioIso || "",
      fecha_fin_contrato: fechaFinIso || "",
    };
    if (fechaFinIso) {
      empleadoPayload.vencimientoContrato = fechaFinIso;
    } else if (isContratoIndefinido) {
      empleadoPayload.vencimientoContrato = "";
    }

    await Promise.all([
      setDoc(doc(db, "users", dniTrabajador), userPayload, { merge: true }),
      setDoc(doc(db, "pl_empleados", dniTrabajador), empleadoPayload, { merge: true }),
    ]);

    return {
      ok: true,
      dniTrabajador,
      fechaInicioIso,
      fechaFinIso,
      isContratoIndefinido,
    };
  };

  const toggleBulkWorker = (workerId) => {
    setSelectedTrabajadorIds((prev) => {
      if (prev.includes(workerId)) return prev.filter((id) => id !== workerId);
      return [...prev, workerId];
    });
  };

  const selectAllWorkers = () => {
    setSelectedTrabajadorIds(trabajadores.map((worker) => worker.id));
  };

  const clearWorkersSelection = () => {
    setSelectedTrabajadorIds([]);
  };

  const applyFirstBulkWorkerToPreview = () => {
    if (!selectedTrabajadoresBulk.length) return;
    const firstWorker = selectedTrabajadoresBulk[0];
    setDatos((prev) => buildDatosForWorker(prev, firstWorker));
    setSaveMessage(`Vista previa cargada con: ${getFullWorkerName(firstWorker) || firstWorker.id}.`);
  };

  const exportPdf = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      if (generationMode === "MASIVO") {
        if (!selectedTrabajadoresBulk.length) {
          setSaveMessage("Selecciona al menos un trabajador para generación masiva.");
          return;
        }

        let generatedCount = 0;
        let persistedCount = 0;
        let missingDniCount = 0;
        let failedCount = 0;

        for (const worker of selectedTrabajadoresBulk) {
          const contractData = buildDatosForWorker(datos, worker);
          const sections = buildContratoSections(clausulas, contractData, templateName, {
            boldVariables: true,
            contractTitle,
          });

          buildContratoPdfHtml(sections, {
            innerPaddingPx: selectedPdfMargin.innerPaddingPx,
          });
          exportPdfFallback(sections, buildPdfFileName(contractData));
          generatedCount += 1;

          try {
            const persisted = await persistContractDates(contractData);
            if (persisted.ok) persistedCount += 1;
            if (!persisted.ok && persisted.reason === "missing_dni") missingDniCount += 1;
          } catch (persistError) {
            failedCount += 1;
            console.error("Error guardando fechas de contrato masivo:", persistError);
          }
        }

        setSaveMessage(
          `Generación masiva completada: ${generatedCount} PDF(s)` +
          ` | Fechas guardadas: ${persistedCount}` +
          (missingDniCount ? ` | Sin DNI: ${missingDniCount}` : "") +
          (failedCount ? ` | Errores de guardado: ${failedCount}` : "")
        );

        // AUDITORÍA
        try {
          await logBusinessAction("GENERACION_MASIVA_CONTRATOS", "GestionDocumentos", {
            plantilla: templateName,
            generados: generatedCount,
            guardados: persistedCount,
            errores: failedCount
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría masiva", e);
        }

        return;
      }

      // Mantiene la misma preparación de contenido que usa la vista previa.
      buildContratoPdfHtml(contratoSections, {
        innerPaddingPx: selectedPdfMargin.innerPaddingPx,
      });
      exportPdfFallback(contratoSections, buildPdfFileName(datos));

      const persisted = await persistContractDates(datos);
      if (!persisted.ok) {
        setSaveMessage("PDF generado. No se guardaron fechas de contrato porque falta DNI del trabajador.");
        return;
      }

      setSaveMessage(
        `PDF generado y fechas guardadas para DNI ${persisted.dniTrabajador}` +
        (persisted.fechaInicioIso ? ` | Inicio: ${persisted.fechaInicioIso}` : "") +
        (persisted.fechaFinIso
          ? ` | Fin: ${persisted.fechaFinIso}`
          : persisted.isContratoIndefinido
            ? " | Contrato indefinido"
            : "")
      );
    } catch (error) {
      console.error("Error generando PDF clásico:", error);
      setSaveMessage("No se pudo completar la generación/guardado del contrato.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleIngestaInteligente = async () => {
    if (!pdfFile) {
      setIngestaError("Selecciona un PDF antes de analizar.");
      return;
    }

    if (!pdfJsLoaded) {
      setIngestaError("PDF.js aún no está listo. Espera unos segundos e intenta nuevamente.");
      return;
    }

    try {
      setAnalizando(true);
      setIngestaError("");
      setIngestaStatus("Extrayendo texto del PDF...");

      const text = await extractTextFromPdf(pdfFile);
      if (!text || text.length < 40) {
        throw new Error("No se pudo extraer suficiente texto del PDF.");
      }

      setIngestaStatus("Analizando con Gemini y separando cláusulas...");
      const parsed = await parseContractTemplateWithGemini({
        contractText: text,
        companyContext: {
          empresa: usuario?.empresa || usuario?.empresaNombreComercial || "",
          ruc: usuario?.ruc || "",
          pais: "Peru",
        },
      });

      if (!parsed.clauses.length) {
        throw new Error("Gemini no devolvió cláusulas válidas para este documento.");
      }

      setTemplateName(parsed.templateName || "CONTRATO DE TRABAJO");
      setContractTitle(
        extractOriginalContractTitle(parsed.templateName || "CONTRATO DE TRABAJO", parsed.clauses) ||
        parsed.templateName ||
        "CONTRATO DE TRABAJO"
      );
      setContractType(parsed.contractType || "GENERAL");
      setLegalAlerts(parsed.legalAlerts || []);
      setClausulas(
        parsed.clauses.map((clause, index) => ({
          ...clause,
          id: clause.id || `clausula-${Date.now()}-${index}`,
          content: normalizeParsedClauseContent(clause.title, clause.content),
        }))
      );
      const rawNormalizedVariables = parsed.variables.map((v, index) => {
        const rawLabel = v.label || v.key || v.sourceText || `variable_${index + 1}`;
        const normalizedKey = canonicalizeContractVariableKey(v.key || rawLabel || v.sourceText);
        return {
          key: normalizedKey || `variable_${index + 1}`,
          label: variableLabelFor(normalizedKey, rawLabel),
          sourceText: v.sourceText || "",
          confirmed: v.confirmed !== false,
        };
      });

      const dedupedVariablesMap = new Map();
      rawNormalizedVariables.forEach((v) => {
        if (!v.key) return;
        const current = dedupedVariablesMap.get(v.key);
        if (!current) {
          dedupedVariablesMap.set(v.key, v);
          return;
        }
        if (!current.sourceText && v.sourceText) {
          dedupedVariablesMap.set(v.key, v);
        }
      });

      const normalizedVariables = Array.from(dedupedVariablesMap.values());

      setVariablesConfig(normalizedVariables);

      setDatos((prev) => {
        const next = { ...prev };
        normalizedVariables.forEach((v) => {
          const key = canonicalizeContractVariableKey(v.key || v.label || v.sourceText);
          if (!key) return;

          const detectedValue = String(v.sourceText || "").trim();

          if (typeof next[key] === "undefined") {
            next[key] = detectedValue || "";
          } else if (!String(next[key] || "").trim() && detectedValue) {
            next[key] = detectedValue;
          }
        });

        return next;
      });

      setIngestaStatus("Contrato parseado. Revisa y confirma variables.");
      setActiveStep("paso-2");
    } catch (error) {
      setIngestaError(error.message || "Error durante la ingesta inteligente.");
      setIngestaStatus("");
    } finally {
      setAnalizando(false);
    }
  };

  const currentStepIndex = Math.max(0, visibleStepKeys.indexOf(activeStep));
  const canGoPrev = currentStepIndex > 0;
  const canGoNext = currentStepIndex < visibleStepKeys.length - 1;

  const goToPrevStep = () => {
    if (!canGoPrev) return;
    setActiveStep(visibleStepKeys[currentStepIndex - 1]);
  };

  const goToNextStep = () => {
    if (!canGoNext) return;
    setActiveStep(visibleStepKeys[currentStepIndex + 1]);
  };

  const introClause = clausulas.find(isIntroClause) || null;
  const editableClauses = clausulas.filter((clause) => !isIntroClause(clause) && !isSignatureClause(clause));

  const createIntroClause = (initial = {}) => {
    const introId = `clausula-introduccion-${Date.now()}`;
    setClausulas((prev) => [
      {
        id: introId,
        title: initial.title || "INTRODUCCIÓN",
        content: initial.content || "",
      },
      ...prev,
    ]);
  };

  const updateIntroClause = (field, value) => {
    if (!introClause) {
      createIntroClause(field === "title" ? { title: value } : { content: value });
      return;
    }
    updateClausula(introClause.id, field, value);
  };

  const renderStepNavigation = () => (
    visibleStepKeys.length <= 1 ? null :
      <Stack direction="horizontal" className="justify-content-between mt-3">
        <Button variant="outline-secondary" onClick={goToPrevStep} disabled={!canGoPrev}>
          Anterior
        </Button>
        <small className="text-muted align-self-center">
          Paso {currentStepIndex + 1} de {visibleStepKeys.length}
        </small>
        <Button variant="primary" onClick={goToNextStep} disabled={!canGoNext}>
          Siguiente
        </Button>
      </Stack>
  );
  const stepCardClassName = "border-0 shadow-sm";
  const stepCardStyle = { borderRadius: "15px" };
  const stepHeaderStyle = {
    background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
    color: "white",
    borderRadius: "15px 15px 0 0",
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                {isGenerateFlow ? "Generación de Contratos desde Plantilla" : "Constructor de Contratos por Bloques"}
              </h2>
              <p className="mb-0 opacity-75">
                Empresa {usuario?.empresa || usuario?.empresaNombreComercial || "Sin nombre"} ({usuario?.ruc || "Sin RUC"})
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Stack direction="horizontal" className="justify-content-between align-items-center flex-wrap" gap={2}>
                  <div>
                    <h5 className="mb-1 fw-semibold">
                      {isGenerateFlow ? "Flujo de Generación" : "Flujo Guiado de Contratos"}
                    </h5>
                    <small className="text-muted">
                      {isGenerateFlow
                        ? "Selecciona trabajadores y genera contratos en lote desde la plantilla elegida."
                        : "Analiza, valida, edita, firma y genera el PDF final del contrato."}
                    </small>
                  </div>
                  <Badge bg="dark">Paso {currentStepIndex + 1} de {visibleStepKeys.length}</Badge>
                </Stack>
              </Card.Body>
            </Card>

            {saveMessage && <Alert variant="success" className="mb-4">{saveMessage}</Alert>}

            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Tabs
                  id="contrato-builder-tabs"
                  activeKey={activeStep}
                  onSelect={(key) => key && setActiveStep(key)}
                  className="mb-3"
                  mountOnEnter
                  unmountOnExit={false}
                >
                  <Tab eventKey="paso-1" title="1) Analizar PDF">
                    <Row className="g-3">
                      <Col lg={5}>
                        <Card className={stepCardClassName} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Ingesta Inteligente de PDF</Card.Header>
                          <Card.Body>
                            <Row className="g-2 align-items-end">
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold mb-1">Subir modelo de contrato (PDF)</Form.Label>
                                  <Form.Control
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={7}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold mb-1">Nombre de plantilla</Form.Label>
                                  <Form.Control value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                                </Form.Group>
                              </Col>
                              <Col md={5}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold mb-1">Tipo de contrato</Form.Label>
                                  <Form.Select value={contractType} onChange={(e) => setContractType(e.target.value)}>
                                    <option value="GENERAL">GENERAL</option>
                                    <option value="PLAZO_FIJO">PLAZO_FIJO</option>
                                    <option value="INDEFINIDO">INDEFINIDO</option>
                                    <option value="SUPLENCIA">SUPLENCIA</option>
                                    <option value="TEMPORAL">TEMPORAL</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>

                            <Stack direction="horizontal" gap={2} className="mt-3 flex-wrap">
                              <Button variant="dark" onClick={handleIngestaInteligente} disabled={analizando || !pdfFile}>
                                {analizando ? <Spinner size="sm" className="me-1" /> : <FaMagic className="me-1" />}
                                Analizar con Axis AI
                              </Button>
                              <Badge bg={pdfJsLoaded ? "success" : "warning"}>
                                {pdfJsLoaded ? "PDF.js listo" : "Cargando PDF.js"}
                              </Badge>
                              {pdfFile && (
                                <Badge bg="info" text="dark">
                                  <FaUpload className="me-1" /> {pdfFile.name}
                                </Badge>
                              )}
                            </Stack>

                            {ingestaStatus && <Alert variant="info" className="mt-3 mb-0">{ingestaStatus}</Alert>}
                            {ingestaError && <Alert variant="danger" className="mt-3 mb-0">{ingestaError}</Alert>}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col lg={7}>
                        <Card className={stepCardClassName} style={{ ...stepCardStyle, minHeight: "620px" }}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Revisión PDF Original</Card.Header>
                          <Card.Body>
                            {pdfUrl ? (
                              <iframe
                                title="pdf-original"
                                src={pdfUrl}
                                style={{ width: "100%", height: "72vh", border: "1px solid #ddd" }}
                              />
                            ) : (
                              <div className="text-muted">Sube un PDF para mostrar la vista original.</div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    {renderStepNavigation()}
                  </Tab>

                  <Tab eventKey="paso-2" title="2) Variables">
                    <Row className="g-3">
                      <Col xl={8}>
                        <Card className={stepCardClassName} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Confirmación de Variables</Card.Header>
                          <Card.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Stack gap={2}>
                              {variablesConfig.map((variable) => (
                                <Card key={variable.key} className="border">
                                  <Card.Body className="py-2">
                                    <Row className="g-2 align-items-center">
                                      <Col md={6}>
                                        <Form.Label className="mb-1" style={{ fontSize: "0.82rem" }}>Clave</Form.Label>
                                        <Form.Control size="sm" value={variable.key} disabled />
                                      </Col>
                                      <Col md={6}>
                                        <Form.Label className="mb-1" style={{ fontSize: "0.82rem" }}>Etiqueta</Form.Label>
                                        <Form.Control
                                          size="sm"
                                          value={variable.label}
                                          onChange={(e) => updateVariableLabel(variable.key, e.target.value)}
                                        />
                                      </Col>
                                    </Row>
                                    <Form.Check
                                      className="mt-2"
                                      type="switch"
                                      label="Confirmar como variable de base de datos"
                                      checked={variable.confirmed}
                                      onChange={() => toggleVariableConfirmed(variable.key)}
                                    />
                                    {variable.sourceText && (
                                      <small className="text-muted d-block mt-1">Detectado desde: {variable.sourceText}</small>
                                    )}
                                  </Card.Body>
                                </Card>
                              ))}
                            </Stack>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xl={4}>
                        <Card className={`${stepCardClassName} h-100`} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Alertas Legales</Card.Header>
                          <Card.Body>
                            {legalAlerts.length ? (
                              <Stack gap={2}>
                                {legalAlerts.map((alerta, idx) => (
                                  <Alert
                                    key={`${alerta.severity}-${idx}`}
                                    variant={
                                      alerta.severity === "ALTO"
                                        ? "danger"
                                        : alerta.severity === "MEDIO"
                                          ? "warning"
                                          : "secondary"
                                    }
                                    className="mb-0 py-2"
                                  >
                                    <strong>{alerta.severity}:</strong> {alerta.message}
                                    {alerta.reference ? ` (${alerta.reference})` : ""}
                                  </Alert>
                                ))}
                              </Stack>
                            ) : (
                              <div className="text-muted">Sin alertas detectadas para este documento.</div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    {renderStepNavigation()}
                  </Tab>

                  <Tab eventKey="paso-3" title="3) Título e Introducción">
                    <Card className={stepCardClassName} style={stepCardStyle}>
                      <Card.Header className="fw-semibold" style={stepHeaderStyle}>Edición de Encabezado del Contrato</Card.Header>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-1">Nombre de plantilla</Form.Label>
                              <Form.Control value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-1">Tipo de contrato</Form.Label>
                              <Form.Select value={contractType} onChange={(e) => setContractType(e.target.value)}>
                                <option value="GENERAL">GENERAL</option>
                                <option value="PLAZO_FIJO">PLAZO_FIJO</option>
                                <option value="INDEFINIDO">INDEFINIDO</option>
                                <option value="SUPLENCIA">SUPLENCIA</option>
                                <option value="TEMPORAL">TEMPORAL</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="g-3 mt-1 mb-2">
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-1">Título del contrato (editable)</Form.Label>
                              <div style={{ background: "#fff" }}>
                                <ReactQuill
                                  theme="snow"
                                  modules={QUILL_MODULES}
                                  formats={QUILL_FORMATS}
                                  value={plainTextToEditorHtml(contractTitle)}
                                  onChange={(content) => setContractTitle(normalizeQuillHtml(content))}
                                  placeholder="Edita el título del contrato..."
                                  style={{ height: "120px", marginBottom: "42px" }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        {!introClause ? (
                          <Alert variant="warning" className="mb-0">
                            No se detectó bloque de introducción.
                            <Button className="ms-2" size="sm" variant="outline-dark" onClick={() => createIntroClause()}>
                              Crear Introducción
                            </Button>
                          </Alert>
                        ) : (
                          <Row className="g-3">
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Título del bloque de introducción</Form.Label>
                                <Form.Control
                                  value={introClause.title}
                                  onChange={(e) => updateIntroClause("title", e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Contenido de introducción</Form.Label>
                                <div style={{ background: "#fff" }}>
                                  <ReactQuill
                                    theme="snow"
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    value={plainTextToEditorHtml(introClause.content)}
                                    onChange={(content) => updateIntroClause("content", normalizeQuillHtml(content))}
                                    placeholder="Escribe la introducción del contrato..."
                                    style={{ height: "240px", marginBottom: "42px" }}
                                  />
                                </div>
                                <small className="text-muted d-block mt-1">
                                  Puedes mantener placeholders como {`{{trabajador_nombre}}`} dentro del editor.
                                </small>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                    {renderStepNavigation()}
                  </Tab>

                  <Tab eventKey="paso-4" title="4) Cláusulas">
                    <Card className={stepCardClassName} style={stepCardStyle}>
                      <Card.Header className="fw-semibold" style={stepHeaderStyle}>Edición de Cláusulas</Card.Header>
                      <Card.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
                        <Stack gap={3}>
                          {editableClauses.map((clausula, index) => {
                            const originalIndex = clausulas.findIndex((c) => c.id === clausula.id);
                            return (
                              <Card key={clausula.id} className="border">
                                <Card.Body>
                                  <Stack direction="horizontal" className="justify-content-between mb-2">
                                    <Badge bg="secondary">Cláusula {index + 1}</Badge>
                                    <Stack direction="horizontal" gap={1}>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => moveClausula(originalIndex, "up")}
                                        disabled={index === 0}
                                      >
                                        <FaArrowUp />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => moveClausula(originalIndex, "down")}
                                        disabled={index === editableClauses.length - 1}
                                      >
                                        <FaArrowDown />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => removeClausula(clausula.id)}
                                        disabled={editableClauses.length === 1}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </Stack>
                                  </Stack>

                                  <Form.Group>
                                    <Form.Label className="fw-semibold mb-1">Contenido</Form.Label>
                                    <div style={{ background: "#fff" }}>
                                      <ReactQuill
                                        theme="snow"
                                        modules={QUILL_MODULES}
                                        formats={QUILL_FORMATS}
                                        value={plainTextToEditorHtml(clausula.content)}
                                        onChange={(content) =>
                                          updateClausula(clausula.id, "content", normalizeQuillHtml(content))
                                        }
                                        placeholder="Edita el cuerpo de la cláusula..."
                                        style={{ height: "220px", marginBottom: "42px" }}
                                      />
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                      Los placeholders {`{{variable}}`} se conservan al guardar.
                                    </small>
                                  </Form.Group>

                                  <div className="mt-2 p-2" style={{ background: "#f8f9fa", borderRadius: 6, fontSize: "0.92rem" }}>
                                    <div>{renderRichParagraphs(clausula.content)}</div>
                                  </div>

                                  <div className="mt-2">
                                    <small className="text-muted d-block mb-1">Insertar placeholder rápido:</small>
                                    <Stack direction="horizontal" gap={1} className="flex-wrap">
                                      {placeholdersDetectados.slice(0, 12).map((token) => (
                                        <Button
                                          key={`${clausula.id}-${token}`}
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => insertToken(clausula.id, token)}
                                        >
                                          {`{{${token}}}`}
                                        </Button>
                                      ))}
                                    </Stack>
                                  </div>
                                </Card.Body>
                              </Card>
                            );
                          })}

                          <Button variant="outline-primary" onClick={addClausula}>
                            <FaPlus className="me-1" /> Agregar cláusula
                          </Button>
                        </Stack>
                      </Card.Body>
                    </Card>
                    {renderStepNavigation()}
                  </Tab>

                  <Tab eventKey="paso-5" title={isGenerateFlow ? "Generar Contratos" : "5) Firmas y Guardar"}>
                    <Row className="g-3">
                      <Col xl={4}>
                        <Card className={`${stepCardClassName} mb-3`} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Seleccionar Trabajador</Card.Header>
                          <Card.Body>
                            {trabajadoresError && (
                              <Alert variant="warning" className="py-2 mb-2">
                                {trabajadoresError}
                              </Alert>
                            )}
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Modo de generación</Form.Label>
                              <Form.Select
                                value={generationMode}
                                onChange={(e) => setGenerationMode(e.target.value)}
                              >
                                <option value="INDIVIDUAL">Individual</option>
                                <option value="MASIVO">Masivo (lote)</option>
                              </Form.Select>
                            </Form.Group>
                            <Form.Group>
                              <Form.Label className="mb-1">
                                {generationMode === "MASIVO"
                                  ? "Seleccionar trabajadores del lote"
                                  : "Autocompletar variables desde planillas"}
                              </Form.Label>
                              {generationMode === "MASIVO" ? (
                                <div className="border rounded p-2" style={{ maxHeight: "230px", overflowY: "auto", background: "#fff" }}>
                                  {!trabajadores.length ? (
                                    <div className="text-muted small">Sin trabajadores disponibles</div>
                                  ) : (
                                    <Stack gap={1}>
                                      {trabajadores.map((worker) => {
                                        const fullName = getFullWorkerName(worker);
                                        const workerDni = toSafeText(worker.dni || worker.id);
                                        return (
                                          <Form.Check
                                            key={worker.id}
                                            type="checkbox"
                                            id={`bulk-worker-${worker.id}`}
                                            label={`${fullName || "Sin nombre"} · DNI ${workerDni}`}
                                            checked={selectedTrabajadorIds.includes(worker.id)}
                                            onChange={() => toggleBulkWorker(worker.id)}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </div>
                              ) : (
                                <Form.Select
                                  value={selectedTrabajadorId}
                                  onChange={(e) => setSelectedTrabajadorId(e.target.value)}
                                  disabled={loadingTrabajadores || !trabajadores.length}
                                >
                                  {!trabajadores.length ? (
                                    <option value="">Sin trabajadores disponibles</option>
                                  ) : (
                                    trabajadores.map((worker) => {
                                      const fullName = getFullWorkerName(worker);
                                      return (
                                        <option key={worker.id} value={worker.id}>
                                          {fullName || "Sin nombre"} · DNI {toSafeText(worker.dni || worker.id)}
                                        </option>
                                      );
                                    })
                                  )}
                                </Form.Select>
                              )}
                            </Form.Group>
                            <Stack direction="horizontal" gap={2} className="mt-2">
                              {generationMode === "MASIVO" ? (
                                <>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={selectAllWorkers}
                                    disabled={loadingTrabajadores || !trabajadores.length}
                                  >
                                    Seleccionar todos
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={clearWorkersSelection}
                                    disabled={!selectedTrabajadorIds.length}
                                  >
                                    Limpiar
                                  </Button>
                                  <Button
                                    variant="outline-dark"
                                    size="sm"
                                    onClick={applyFirstBulkWorkerToPreview}
                                    disabled={!selectedTrabajadoresBulk.length}
                                  >
                                    Cargar 1ro a vista previa
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={applySelectedWorkerData}
                                  disabled={!selectedTrabajadorId || loadingTrabajadores}
                                >
                                  Aplicar datos del trabajador
                                </Button>
                              )}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={loadTrabajadores}
                                disabled={loadingTrabajadores}
                              >
                                {loadingTrabajadores ? <Spinner size="sm" className="me-1" /> : null}
                                Actualizar lista
                              </Button>
                            </Stack>
                            <small className="text-muted d-block mt-2">
                              {generationMode === "MASIVO"
                                ? `Se generará un PDF por cada trabajador seleccionado (${selectedTrabajadorIds.length} seleccionados de ${trabajadores.length}).`
                                : `Completa automáticamente nombre, DNI, cargo, sueldo y firma del trabajador (${trabajadores.length} cargados).`}
                            </small>
                          </Card.Body>
                        </Card>

                        {!isGenerateFlow && (
                          <Card className={`${stepCardClassName} mb-3`} style={stepCardStyle}>
                            <Card.Header className="fw-semibold" style={stepHeaderStyle}>Datos para Completar Variables</Card.Header>
                            <Card.Body style={{ maxHeight: "40vh", overflowY: "auto" }}>
                              <Row className="g-2">
                                {placeholdersDetectados.map((token) => (
                                  <Col md={12} key={token}>
                                    <Form.Group>
                                      <Form.Label className="mb-1" style={{ fontSize: "0.82rem" }}>{token}</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        value={datos[token] || ""}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setDatos((prev) => {
                                            const updated = { ...prev, [token]: val };
                                            if (token === "representante_legal") updated.firma_empleador_nombre = val;
                                            if (token === "firma_empleador_nombre") updated.representante_legal = val;
                                            if (token === "representante_legal_cargo") updated.firma_empleador_cargo = val;
                                            if (token === "firma_empleador_cargo") updated.representante_legal_cargo = val;
                                            if (token === "fecha_inicio" && val) {
                                              const finPrueba = calcularFinPeriodoPrueba(val, prev.periodo_prueba_meses || 3);
                                              if (finPrueba) updated.fecha_fin_periodo_prueba = formatDateEsPe(finPrueba);
                                            }
                                            return updated;
                                          });
                                        }}
                                      />
                                    </Form.Group>
                                  </Col>
                                ))}
                              </Row>
                            </Card.Body>
                          </Card>
                        )}

                        <Card className={`${stepCardClassName} mb-3`} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Vigencia del Contrato</Card.Header>
                          <Card.Body>
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Fecha de inicio</Form.Label>
                              <Form.Control
                                type="date"
                                value={fechaInicioInputValue}
                                onChange={(e) => {
                                  const nextFechaInicio = e.target.value;
                                  const nextFinPrueba = nextFechaInicio ? calcularFinPeriodoPrueba(nextFechaInicio, datos.periodo_prueba_meses || 3) : "";
                                  setDatos((prev) => ({
                                    ...prev,
                                    fecha_inicio: nextFechaInicio,
                                    fecha_fin_periodo_prueba: nextFinPrueba ? formatDateEsPe(nextFinPrueba) : prev.fecha_fin_periodo_prueba,
                                  }));
                                }}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Fecha de fin</Form.Label>
                              <Form.Control
                                type="date"
                                value={fechaFinInputValue}
                                onChange={(e) => setDatos((prev) => ({ ...prev, fecha_fin: e.target.value }))}
                              />
                            </Form.Group>
                            <Form.Group>
                              <Form.Label className="mb-1 text-muted small">Término de periodo de prueba (automático +3 meses)</Form.Label>
                              <Form.Control
                                size="sm"
                                value={datos.fecha_fin_periodo_prueba || (datos.fecha_inicio ? formatDateEsPe(calcularFinPeriodoPrueba(datos.fecha_inicio)) : "")}
                                onChange={(e) => setDatos((prev) => ({ ...prev, fecha_fin_periodo_prueba: e.target.value }))}
                                placeholder="DD/MM/YYYY"
                              />
                            </Form.Group>
                            <small className="text-muted d-block mt-2">
                              Si el contrato es indefinido, deja la fecha de fin en blanco.
                            </small>
                          </Card.Body>
                        </Card>

                        <Card className={`${stepCardClassName} mb-3`} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Edición de Firmas</Card.Header>
                          <Card.Body>
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Representante legal (empleador)</Form.Label>
                              <Form.Control
                                value={datos.representante_legal || datos.firma_empleador_nombre || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setDatos((prev) => ({
                                    ...prev,
                                    representante_legal: val,
                                    firma_empleador_nombre: val,
                                  }));
                                }}
                                placeholder="Nombre del representante legal"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Cargo representante legal</Form.Label>
                              <Form.Control
                                value={datos.representante_legal_cargo || datos.firma_empleador_cargo || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setDatos((prev) => ({
                                    ...prev,
                                    representante_legal_cargo: val,
                                    firma_empleador_cargo: val,
                                  }));
                                }}
                                placeholder="Ej: Gerente General"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="mb-1">Trabajador (firma)</Form.Label>
                              <Form.Control
                                value={datos.firma_trabajador_nombre || ""}
                                onChange={(e) => setDatos((prev) => ({ ...prev, firma_trabajador_nombre: e.target.value }))}
                              />
                            </Form.Group>
                            <Form.Group>
                              <Form.Label className="mb-1">DNI trabajador (firma)</Form.Label>
                              <Form.Control
                                value={datos.firma_trabajador_dni || ""}
                                onChange={(e) => setDatos((prev) => ({ ...prev, firma_trabajador_dni: e.target.value }))}
                              />
                            </Form.Group>
                          </Card.Body>
                        </Card>

                        <Card className={`${stepCardClassName} mb-3`} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>Formato PDF (A4)</Card.Header>
                          <Card.Body>
                            <Form.Group>
                              <Form.Label className="mb-1">Márgenes de página</Form.Label>
                              <Form.Select
                                value={pdfMarginPreset}
                                onChange={(e) => setPdfMarginPreset(e.target.value)}
                              >
                                {Object.entries(PDF_MARGIN_PRESETS).map(([key, preset]) => (
                                  <option key={key} value={key}>
                                    {preset.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <small className="text-muted d-block mt-2">
                              Este ajuste se aplica al PDF generado y se guarda con la plantilla.
                            </small>
                          </Card.Body>
                        </Card>

                        <Card className={stepCardClassName} style={stepCardStyle}>
                          <Card.Header className="fw-semibold" style={stepHeaderStyle}>
                            {isGenerateFlow ? "Generar Contratos" : "Guardar Plantilla"}
                          </Card.Header>
                          <Card.Body>
                            <Stack gap={2}>
                              {!isGenerateFlow && (
                                <>
                                  <Button variant="outline-secondary" onClick={saveTemplateLocal}>
                                    <FaSave className="me-1" /> Guardar Local
                                  </Button>
                                  <Button variant="outline-success" onClick={saveTemplateCloud}>
                                    <FaSave className="me-1" /> Guardar Plantilla Empresa
                                  </Button>
                                  <Button variant="outline-danger" onClick={resetTemplate}>Restablecer</Button>
                                </>
                              )}
                              <Button
                                variant="primary"
                                onClick={exportPdf}
                                disabled={generatingPdf || (generationMode === "MASIVO" && !selectedTrabajadorIds.length)}
                              >
                                {generatingPdf ? <Spinner size="sm" className="me-1" /> : <FaFilePdf className="me-1" />}
                                {generatingPdf
                                  ? "Generando PDF..."
                                  : generationMode === "MASIVO"
                                    ? `Generar PDFs (${selectedTrabajadorIds.length})`
                                    : "Generar PDF"}
                              </Button>
                            </Stack>
                          </Card.Body>
                        </Card>
                      </Col>

                      <Col xl={8}>
                        <Card className={stepCardClassName} style={stepCardStyle}>
                          <Card.Header style={stepHeaderStyle}>
                            <Stack direction="horizontal" className="justify-content-between align-items-center">
                              <span className="fw-semibold">Vista Previa de Contrato Final</span>
                              <Button
                                size="sm"
                                variant="dark"
                                onClick={exportPdf}
                                disabled={generatingPdf || (generationMode === "MASIVO" && !selectedTrabajadorIds.length)}
                              >
                                {generatingPdf ? <Spinner size="sm" className="me-1" /> : <FaFilePdf className="me-1" />}
                                {generatingPdf
                                  ? "Generando..."
                                  : generationMode === "MASIVO"
                                    ? "Generar lote"
                                    : "Generar PDF de prueba"}
                              </Button>
                            </Stack>
                          </Card.Header>
                          <Card.Body>
                            <div
                              style={{
                                border: "1px solid #e9ecef",
                                borderRadius: 8,
                                padding: "10px 12px",
                                background: "#f8f9fa",
                                marginBottom: 12,
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="fw-semibold">Regla visual A4 (margen aplicado al PDF)</small>
                                <small className="text-muted">
                                  {selectedPdfMargin.label} · ~{sideMarginMm.toFixed(1)} mm por lado
                                </small>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  width: "100%",
                                  height: 16,
                                  borderRadius: 6,
                                  overflow: "hidden",
                                  border: "1px solid #d7dce1",
                                  background: "#fff",
                                }}
                              >
                                <div style={{ width: `${sideMarginPct}%`, background: "#dee2e6" }} />
                                <div style={{ width: `${printableWidthPct}%`, background: "#d1e7dd" }} />
                                <div style={{ width: `${sideMarginPct}%`, background: "#dee2e6" }} />
                              </div>
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-muted">Margen izquierdo</small>
                                <small className="text-success fw-semibold">Área útil: {printableWidthPct.toFixed(1)}%</small>
                                <small className="text-muted">Margen derecho</small>
                              </div>
                            </div>

                            <div
                              style={{
                                maxHeight: "74vh",
                                overflow: "auto",
                                background: "#fff",
                                border: "1px solid #e9ecef",
                                borderRadius: "8px",
                                padding: "14px",
                                fontFamily: "'Times New Roman', serif",
                                fontSize: "0.95rem",
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "center",
                                  fontWeight: 700,
                                  marginBottom: "14px",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {hasRenderableText(contratoSections.titleHtml)
                                  ? (
                                    <div
                                      style={{ textAlign: "center" }}
                                      dangerouslySetInnerHTML={{
                                        __html: normalizeQuillHtml(sanitizeEditorHtml(contratoSections.titleHtml)),
                                      }}
                                    />
                                  )
                                  : contratoSections.title}
                              </div>

                              {hasRenderableText(contratoSections.introHtml)
                                ? renderRichParagraphs(contratoSections.introHtml)
                                : contratoSections.introParagraphs.map((paragraph, idx) => (
                                  <div key={`intro-${idx}`}>{renderRichParagraphs(paragraph)}</div>
                                ))}

                              {contratoSections.clauses.map((clause, clauseIndex) => (
                                <div key={`clause-prev-${clauseIndex}`} style={{ marginBottom: "10px" }}>
                                  {hasRenderableText(clause.html) ? (
                                    renderRichParagraphs(clause.html)
                                  ) : (
                                    clause.paragraphs.map((paragraph, pIndex) => (
                                      <div key={`cp-${clauseIndex}-${pIndex}`}>{renderRichParagraphs(paragraph)}</div>
                                    ))
                                  )}
                                </div>
                              ))}

                              {(contratoSections.signatures.employerName || contratoSections.signatures.employeeName) && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "22px",
                                    marginTop: "36px",
                                    alignItems: "stretch",
                                  }}
                                >
                                  <div style={{ flex: 1, textAlign: "center", minHeight: "120px" }}>
                                    <div style={{ height: "44px" }} />
                                    <div style={{ borderTop: "1px solid #444", margin: "0 16px 8px" }} />
                                    <div style={{ fontWeight: 700, letterSpacing: "0.3px" }}>EL EMPLEADOR</div>
                                    {contratoSections.signatures.employerName && (
                                      <div style={{ marginTop: 4 }}>{renderInlineBold(`**${contratoSections.signatures.employerName}**`)}</div>
                                    )}
                                    {contratoSections.signatures.employerRole && (
                                      <div style={{ marginTop: 2, fontSize: "0.92rem" }}>
                                        {renderInlineBold(`**${contratoSections.signatures.employerRole}**`)}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ flex: 1, textAlign: "center", minHeight: "120px" }}>
                                    <div style={{ height: "44px" }} />
                                    <div style={{ borderTop: "1px solid #444", margin: "0 16px 8px" }} />
                                    <div style={{ fontWeight: 700, letterSpacing: "0.3px" }}>EL TRABAJADOR</div>
                                    {contratoSections.signatures.employeeName && (
                                      <div style={{ marginTop: 4 }}>
                                        {renderInlineBold(`**${contratoSections.signatures.employeeName}**`)}
                                      </div>
                                    )}
                                    {contratoSections.signatures.employeeDni && (
                                      <div style={{ marginTop: 2, fontSize: "0.92rem" }}>
                                        {renderInlineBold(`**DNI: ${contratoSections.signatures.employeeDni}**`)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    {renderStepNavigation()}
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default GeneradorContratos;
